import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  setCenters as setReduxCenters,
  setCentersLoading,
  setCentersError,
  setSelectedCenter as setReduxSelectedCenter,
} from '../../../redux/slices/retreatSlice';
import retreatService from '../services/retreatService';
import './RetreatCenter.css';

const RetreatCenter = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fetchingRef = useRef(false);

  const { centers, centersLoading, centersError, selectedCenter } = useSelector((state) => state.retreat);

  const [countryId, setCountryId] = useState('USA');

  useEffect(() => {
    if (fetchingRef.current) return;
    if (centers.length > 0) {
      fetchCenters(countryId, true);
    } else {
      fetchCenters(countryId, false);
    }
  }, [countryId]);

  const fetchCenters = async (country, silent = false) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      if (!silent) dispatch(setCentersLoading(true));
      const data = await retreatService.getCenters(country);
      dispatch(setReduxCenters(data || []));
      dispatch(setCentersError(null));
    } catch (err) {
      if (!silent) dispatch(setCentersError('Failed to load centers. Please try again later.'));
      console.error(err);
    } finally {
      if (!silent) dispatch(setCentersLoading(false));
      fetchingRef.current = false;
    }
  };

  const handleSelectCenter = (center) => {
    dispatch(setReduxSelectedCenter({
      center_Id: center.center_Id,
      center_Name: center.center_Name,
      center_Desc: center.center_desc || center.center_Desc,
      center_Logo_URL: center.center_Logo_URL,
      country_Desc: countryId,
    }));
    navigate(`/retreat/selection?centerId=${center.center_Id}&country=${countryId}`);
  };

  return (
    <div className="retreat-center-page min-h-screen py-16">
      <div className="container-custom">

        <div className="retreat-center-header">
          <div>
            <h1 className="retreat-center-title">Select Your Retreat Center</h1>
            <p className="retreat-center-subtitle">Discover peaceful retreats at our centers</p>
          </div>
        </div>

        {centersLoading && (
          <div className="retreat-center-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            <p>Loading centers...</p>
          </div>
        )}

        {centersError && (
          <div className="retreat-center-error">
            <p>{centersError}</p>
            <button onClick={() => fetchCenters(countryId, false)}>Try again</button>
          </div>
        )}

        {!centersLoading && centers.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {centers.map((center) => (
              <div
                key={center.center_Id}
                className={`item-card cursor-pointer flex flex-col ${selectedCenter?.center_Id === center.center_Id ? 'selected' : ''}`}
                onClick={() => handleSelectCenter(center)}
              >
                {selectedCenter?.center_Id === center.center_Id && (
                  <div className="absolute top-3 right-3 z-20 w-9 h-9 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}

                {center.center_Logo_URL && (
                  <div className="h-56 overflow-hidden relative rounded-t-[18px]">
                    <img
                      src={center.center_Logo_URL?.trim()}
                      alt={center.center_Name}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                      onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300?text=Retreat+Center'; }}
                    />
                  </div>
                )}

                <div className="p-6 relative z-10 flex flex-col flex-1">
                  <h3 className="text-base font-semibold text-gray-800 mb-3 line-clamp-2 leading-snug">
                    {center.center_Name}
                  </h3>
                  <p className="retreat-center-desc flex-1">
                    {center.center_desc || center.center_Desc}
                  </p>
                  <button className="retreat-center-btn mt-auto">Explore Retreats</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {!centersLoading && centers.length === 0 && !centersError && (
          <div className="retreat-center-empty">
            <div className="retreat-center-empty-icon">🧘</div>
            <p>No retreat centers available.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RetreatCenter;
